const EMAIL = "info@vaya-transfer.com";

export const PRIVACY_UPDATED = "2026-06-01";

export const privacyPolicy = {
  en: [
    {
      title: "Who we are",
      body: [
        'Vaya Hotel Transfers ("Vaya Transfer", "we", "us") provides private and shared airport transfers between Antalya Airport (AYT), Gazipaşa–Alanya Airport (GZP) and hotels and addresses in the region between Kemer and Gazipaşa. We operate this website and the Vaya Transfer mobile application.',
        `We are the data controller for the personal data described in this policy. You can reach us at ${EMAIL}.`,
      ],
    },
    {
      title: "What data we collect",
      body: [
        "Account data: name, e-mail address, phone number, password (stored only as a cryptographic hash), preferred language and account role (traveller, driver, agency).",
        "Booking data: pick-up and drop-off location, date and time, number of passengers and luggage, vehicle class, flight number, notes to the driver, booking code and driver PIN, price and payment method (cash on arrival).",
        "Driver data: for drivers and suppliers, job assignments, ride status updates, commission and payout figures, and — only while a ride is active and only if you allow it — the vehicle's GPS position, so the traveller can see the driver approaching.",
        "Support data: the messages you send to our travel assistant and support team.",
        "Technical data: IP address, browser and device type, and basic usage events needed to keep the service secure and working.",
        "We do not collect card details on this website; transfers are paid in cash to the driver.",
      ],
    },
    {
      title: "Why we use it and on what legal basis",
      body: [
        "To perform our contract with you: creating your account, producing quotes, confirming bookings, dispatching a driver, sharing the driver's details and live position, and handling cancellations.",
        "Legitimate interests: preventing fraud and abuse, securing the platform, monitoring flight arrivals so the driver waits for delayed flights, and improving our routes, pricing and fleet planning using aggregated statistics.",
        "Legal obligation: keeping accounting, tax and transport records required by Turkish law.",
        "Consent: device location sharing by drivers, optional marketing messages, and non-essential cookies. You can withdraw consent at any time.",
      ],
    },
    {
      title: "Who we share it with",
      body: [
        "We share only the data needed for your ride to happen: the assigned driver or partner supplier receives your name, phone number, pick-up and drop-off details, flight number and passenger count. Travel agencies see the bookings they made themselves.",
        "We also use service providers acting on our instructions: cloud hosting and database providers, e-mail delivery, flight-status data providers and the AI provider that powers our travel assistant. We never sell your personal data.",
        "Some of these providers process data outside Turkey and the EU/EEA; where that happens we rely on adequacy decisions or standard contractual clauses.",
      ],
    },
    {
      title: "How long we keep it",
      body: [
        "Booking and invoicing records are kept for 10 years to meet Turkish accounting and tax requirements. Account data is kept while your account exists and for up to 12 months after you ask us to delete it, so we can resolve disputes. Live GPS positions are deleted shortly after the ride is completed. Support conversations are kept for up to 24 months.",
      ],
    },
    {
      title: "Your rights",
      body: [
        `Under the Turkish Personal Data Protection Law (KVKK No. 6698) and, where it applies, the EU GDPR, you may request access to your data, correction of inaccurate data, deletion, restriction of or objection to processing, a portable copy of your data, and withdrawal of consent. Write to ${EMAIL} and we will answer within 30 days. You may also complain to the Turkish Data Protection Authority (KVKK) or your local supervisory authority.`,
      ],
    },
    {
      title: "Cookies and local storage",
      body: [
        "We use browser local storage to keep you signed in (an authentication token), to remember your language choice and to keep your support conversation together. These are strictly necessary for the site to work. We do not use advertising cookies.",
      ],
    },
    {
      title: "Security",
      body: [
        "Traffic is encrypted with HTTPS, passwords are stored only as salted hashes, access to booking data is restricted by role (traveller, driver, agency, administrator) and driver contact details are revealed only for the ride they are assigned to.",
      ],
    },
    {
      title: "Children",
      body: [
        "Our service is not directed at children under 16. Children travel as passengers on a booking made by an adult; we only need the number and age group of passengers so we can provide the correct child seats.",
      ],
    },
    {
      title: "Changes to this policy",
      body: [
        "If we change this policy we will update the date above and, for significant changes, notify you by e-mail or in the app before the change takes effect.",
      ],
    },
  ],

  tr: [
    {
      title: "Kim olduğumuz",
      body: [
        'Vaya Hotel Transfers ("Vaya Transfer", "biz"), Antalya Havalimanı (AYT) ve Gazipaşa–Alanya Havalimanı (GZP) ile Kemer–Gazipaşa arasındaki oteller ve adresler arasında özel ve paylaşımlı havalimanı transferi sunar. Bu web sitesini ve Vaya Transfer mobil uygulamasını biz işletiyoruz.',
        `Bu politikada açıklanan kişisel veriler bakımından veri sorumlusu biziz. Bize ${EMAIL} adresinden ulaşabilirsiniz.`,
      ],
    },
    {
      title: "Hangi verileri topluyoruz",
      body: [
        "Hesap verileri: ad soyad, e-posta adresi, telefon numarası, şifre (yalnızca kriptografik özet olarak saklanır), dil tercihi ve hesap rolü (yolcu, sürücü, ajans).",
        "Rezervasyon verileri: alış ve varış yeri, tarih ve saat, yolcu ve bagaj sayısı, araç sınıfı, uçuş numarası, şoföre notlar, rezervasyon kodu ve şoför PIN'i, fiyat ve ödeme yöntemi (varışta nakit).",
        "Sürücü verileri: sürücü ve tedarikçiler için iş atamaları, yolculuk durumu güncellemeleri, komisyon ve ödeme tutarları ve — yalnızca yolculuk sürerken ve yalnızca izin verirseniz — yolcunun şoförün yaklaştığını görebilmesi için aracın GPS konumu.",
        "Destek verileri: seyahat asistanımıza ve destek ekibimize gönderdiğiniz mesajlar.",
        "Teknik veriler: IP adresi, tarayıcı ve cihaz türü ile hizmetin güvenli ve çalışır kalması için gereken temel kullanım kayıtları.",
        "Bu web sitesinde kart bilgisi toplamıyoruz; transferler şoföre nakit ödenir.",
      ],
    },
    {
      title: "Neden kullanıyoruz ve hukuki dayanak",
      body: [
        "Sözleşmenin ifası: hesabınızın oluşturulması, fiyat teklifi verilmesi, rezervasyonun onaylanması, şoför ataması, şoför bilgileri ve canlı konumun paylaşılması ve iptallerin yürütülmesi.",
        "Meşru menfaat: dolandırıcılık ve kötüye kullanımın önlenmesi, platform güvenliği, rötarlı uçuşlarda şoförün beklemesi için uçuş takibi ve toplu istatistiklerle güzergâh, fiyat ve filo planlamasının iyileştirilmesi.",
        "Hukuki yükümlülük: Türk mevzuatının gerektirdiği muhasebe, vergi ve taşımacılık kayıtlarının tutulması.",
        "Açık rıza: sürücülerin cihaz konumu paylaşımı, isteğe bağlı pazarlama iletileri ve zorunlu olmayan çerezler. Rızanızı her zaman geri alabilirsiniz.",
      ],
    },
    {
      title: "Kimlerle paylaşıyoruz",
      body: [
        "Yalnızca yolculuğun gerçekleşmesi için gerekli verileri paylaşırız: atanan şoför veya iş ortağı tedarikçi adınızı, telefon numaranızı, alış-varış bilgilerini, uçuş numarasını ve yolcu sayısını görür. Seyahat ajansları yalnızca kendi yaptıkları rezervasyonları görür.",
        "Talimatlarımızla hareket eden hizmet sağlayıcıları da kullanırız: bulut barındırma ve veritabanı sağlayıcıları, e-posta gönderimi, uçuş durumu veri sağlayıcıları ve seyahat asistanımızı çalıştıran yapay zekâ sağlayıcısı. Kişisel verilerinizi asla satmayız.",
        "Bu sağlayıcıların bazıları verileri Türkiye ve AB/AEA dışında işler; bu durumda yeterlilik kararlarına veya standart sözleşme hükümlerine dayanırız.",
      ],
    },
    {
      title: "Ne kadar süre saklıyoruz",
      body: [
        "Rezervasyon ve fatura kayıtları, Türk muhasebe ve vergi mevzuatı gereği 10 yıl saklanır. Hesap verileri, hesabınız var olduğu sürece ve silme talebinizden sonra uyuşmazlıkların çözümü için en fazla 12 ay saklanır. Canlı GPS konumları yolculuk tamamlandıktan kısa süre sonra silinir. Destek yazışmaları en fazla 24 ay saklanır.",
      ],
    },
    {
      title: "Haklarınız",
      body: [
        `6698 sayılı Kişisel Verilerin Korunması Kanunu (KVKK) ve uygulanabildiği ölçüde AB GDPR kapsamında; verilerinize erişme, yanlış verilerin düzeltilmesini, silinmesini, işlemenin kısıtlanmasını veya işlemeye itiraz etme, verilerinizin taşınabilir bir kopyasını isteme ve rızanızı geri alma haklarına sahipsiniz. ${EMAIL} adresine yazın; 30 gün içinde yanıt veririz. Ayrıca Kişisel Verileri Koruma Kurumu'na (KVKK) veya yerel denetim otoritenize şikâyette bulunabilirsiniz.`,
      ],
    },
    {
      title: "Çerezler ve yerel depolama",
      body: [
        "Oturumunuzu açık tutmak (kimlik doğrulama anahtarı), dil tercihinizi hatırlamak ve destek görüşmenizi bir arada tutmak için tarayıcı yerel depolamasını kullanırız. Bunlar sitenin çalışması için zorunludur. Reklam çerezi kullanmıyoruz.",
      ],
    },
    {
      title: "Güvenlik",
      body: [
        "Trafik HTTPS ile şifrelenir, şifreler yalnızca tuzlanmış özet olarak saklanır, rezervasyon verilerine erişim role göre (yolcu, sürücü, ajans, yönetici) kısıtlanır ve şoför iletişim bilgileri yalnızca atandığı yolculuk için gösterilir.",
      ],
    },
    {
      title: "Çocuklar",
      body: [
        "Hizmetimiz 16 yaş altındaki çocuklara yönelik değildir. Çocuklar, bir yetişkinin yaptığı rezervasyonda yolcu olarak seyahat eder; doğru çocuk koltuğunu sağlamak için yalnızca yolcu sayısı ve yaş grubunu bilmemiz gerekir.",
      ],
    },
    {
      title: "Bu politikadaki değişiklikler",
      body: [
        "Bu politikayı değiştirirsek yukarıdaki tarihi güncelleriz ve önemli değişikliklerde, değişiklik yürürlüğe girmeden önce e-posta ile veya uygulama içinde sizi bilgilendiririz.",
      ],
    },
  ],

  ru: [
    {
      title: "Кто мы",
      body: [
        'Vaya Hotel Transfers («Vaya Transfer», «мы») предоставляет индивидуальные и групповые трансферы между аэропортом Анталии (AYT), аэропортом Газипаша–Алания (GZP) и отелями и адресами в регионе от Кемера до Газипаши. Мы управляем этим сайтом и мобильным приложением Vaya Transfer.',
        `Мы являемся оператором персональных данных, описанных в этой политике. Связаться с нами можно по адресу ${EMAIL}.`,
      ],
    },
    {
      title: "Какие данные мы собираем",
      body: [
        "Данные аккаунта: имя, адрес e-mail, номер телефона, пароль (хранится только в виде криптографического хеша), выбранный язык и роль в системе (пассажир, водитель, агентство).",
        "Данные заказа: место посадки и высадки, дата и время, число пассажиров и багажа, класс автомобиля, номер рейса, пожелания водителю, код заказа и PIN водителя, цена и способ оплаты (наличными по прибытии).",
        "Данные водителя: для водителей и партнёров — назначенные заказы, обновления статуса поездки, комиссия и выплаты, а также — только во время активной поездки и только с вашего разрешения — GPS-положение автомобиля, чтобы пассажир видел приближение водителя.",
        "Данные поддержки: сообщения, которые вы отправляете нашему помощнику и службе поддержки.",
        "Технические данные: IP-адрес, тип браузера и устройства, базовые события использования, необходимые для безопасности и работы сервиса.",
        "Мы не собираем данные банковских карт на этом сайте: трансфер оплачивается водителю наличными.",
      ],
    },
    {
      title: "Зачем мы их используем и на каком основании",
      body: [
        "Исполнение договора с вами: создание аккаунта, расчёт цены, подтверждение заказа, назначение водителя, передача его контактов и геопозиции, обработка отмен.",
        "Законные интересы: предотвращение мошенничества и злоупотреблений, защита платформы, отслеживание прилётов, чтобы водитель ждал при задержке рейса, и улучшение маршрутов, цен и планирования автопарка на основе агрегированной статистики.",
        "Требования закона: ведение бухгалтерской, налоговой и транспортной отчётности согласно законодательству Турции.",
        "Согласие: передача геопозиции устройства водителями, необязательные маркетинговые сообщения и второстепенные cookie. Согласие можно отозвать в любой момент.",
      ],
    },
    {
      title: "С кем мы делимся",
      body: [
        "Мы передаём только данные, необходимые для поездки: назначенный водитель или партнёр получает ваше имя, телефон, детали посадки и высадки, номер рейса и число пассажиров. Турагентства видят только собственные заказы.",
        "Мы также используем поставщиков услуг, действующих по нашим инструкциям: облачный хостинг и базы данных, доставку e-mail, поставщиков данных о рейсах и ИИ-провайдера, обеспечивающего работу помощника. Мы никогда не продаём ваши персональные данные.",
        "Некоторые поставщики обрабатывают данные вне Турции и ЕС/ЕЭЗ; в этом случае мы опираемся на решения об адекватности или стандартные договорные условия.",
      ],
    },
    {
      title: "Сколько мы храним данные",
      body: [
        "Данные заказов и счетов хранятся 10 лет согласно требованиям бухгалтерского и налогового учёта Турции. Данные аккаунта хранятся пока существует аккаунт и до 12 месяцев после запроса на удаление — для разрешения споров. Данные GPS удаляются вскоре после завершения поездки. Переписка с поддержкой хранится до 24 месяцев.",
      ],
    },
    {
      title: "Ваши права",
      body: [
        `Согласно Закону Турции о защите персональных данных (KVKK № 6698) и, где применимо, GDPR ЕС, вы можете запросить доступ к своим данным, их исправление, удаление, ограничение обработки или возражение против неё, копию данных в переносимом формате и отзыв согласия. Напишите на ${EMAIL} — мы ответим в течение 30 дней. Вы также можете обратиться с жалобой в турецкий орган по защите данных (KVKK) или в надзорный орган своей страны.`,
      ],
    },
    {
      title: "Cookie и локальное хранилище",
      body: [
        "Мы используем локальное хранилище браузера, чтобы вы оставались в системе (токен авторизации), запомнить выбранный язык и сохранить историю переписки с поддержкой. Это строго необходимо для работы сайта. Рекламные cookie мы не используем.",
      ],
    },
    {
      title: "Безопасность",
      body: [
        "Трафик шифруется по HTTPS, пароли хранятся только в виде солёных хешей, доступ к данным заказов ограничен по роли (пассажир, водитель, агентство, администратор), а контакты водителя раскрываются только для назначенной ему поездки.",
      ],
    },
    {
      title: "Дети",
      body: [
        "Сервис не предназначен для детей младше 16 лет. Дети едут как пассажиры по заказу, оформленному взрослым; нам нужны только количество и возрастная группа пассажиров, чтобы подготовить подходящие детские кресла.",
      ],
    },
    {
      title: "Изменения политики",
      body: [
        "Если мы изменим эту политику, мы обновим дату выше, а о существенных изменениях сообщим по e-mail или в приложении до вступления их в силу.",
      ],
    },
  ],

  de: [
    {
      title: "Wer wir sind",
      body: [
        'Vaya Hotel Transfers („Vaya Transfer", „wir") bietet private und geteilte Flughafentransfers zwischen dem Flughafen Antalya (AYT), dem Flughafen Gazipaşa–Alanya (GZP) und Hotels und Adressen in der Region zwischen Kemer und Gazipaşa. Wir betreiben diese Website und die Vaya Transfer App.',
        `Für die in dieser Erklärung beschriebenen personenbezogenen Daten sind wir der Verantwortliche. Sie erreichen uns unter ${EMAIL}.`,
      ],
    },
    {
      title: "Welche Daten wir erheben",
      body: [
        "Kontodaten: Name, E-Mail-Adresse, Telefonnummer, Passwort (nur als kryptografischer Hash gespeichert), Sprachwahl und Kontorolle (Reisender, Fahrer, Agentur).",
        "Buchungsdaten: Abhol- und Zielort, Datum und Uhrzeit, Anzahl der Passagiere und Gepäckstücke, Fahrzeugklasse, Flugnummer, Hinweise an den Fahrer, Buchungscode und Fahrer-PIN, Preis und Zahlungsart (Barzahlung bei Ankunft).",
        "Fahrerdaten: für Fahrer und Partner die Auftragszuweisungen, Statusaktualisierungen, Provisions- und Auszahlungsbeträge sowie — nur während einer aktiven Fahrt und nur mit Ihrer Erlaubnis — die GPS-Position des Fahrzeugs, damit der Reisende den herannahenden Fahrer sieht.",
        "Supportdaten: die Nachrichten, die Sie an unseren Reiseassistenten und unser Support-Team senden.",
        "Technische Daten: IP-Adresse, Browser- und Gerätetyp sowie grundlegende Nutzungsereignisse, die für Sicherheit und Betrieb erforderlich sind.",
        "Auf dieser Website erheben wir keine Kartendaten; Transfers werden bar an den Fahrer bezahlt.",
      ],
    },
    {
      title: "Zwecke und Rechtsgrundlagen",
      body: [
        "Vertragserfüllung: Anlegen Ihres Kontos, Preisberechnung, Buchungsbestätigung, Fahrerdisposition, Weitergabe der Fahrerdaten und der Live-Position sowie Bearbeitung von Storno.",
        "Berechtigte Interessen: Verhinderung von Betrug und Missbrauch, Absicherung der Plattform, Überwachung der Flugankünfte, damit der Fahrer bei Verspätungen wartet, und Verbesserung von Strecken, Preisen und Flottenplanung anhand aggregierter Statistiken.",
        "Rechtliche Verpflichtung: Aufbewahrung der nach türkischem Recht erforderlichen Buchhaltungs-, Steuer- und Transportunterlagen.",
        "Einwilligung: Standortfreigabe durch Fahrer, optionale Marketingnachrichten und nicht notwendige Cookies. Sie können Ihre Einwilligung jederzeit widerrufen.",
      ],
    },
    {
      title: "Mit wem wir Daten teilen",
      body: [
        "Wir geben nur die für die Fahrt erforderlichen Daten weiter: Der zugewiesene Fahrer oder Partnerbetrieb erhält Ihren Namen, Ihre Telefonnummer, die Abhol- und Zieldaten, die Flugnummer und die Personenzahl. Reisebüros sehen ausschließlich ihre eigenen Buchungen.",
        "Wir nutzen zudem Dienstleister, die nach unseren Weisungen handeln: Cloud-Hosting und Datenbanken, E-Mail-Versand, Anbieter von Flugstatusdaten und den KI-Anbieter unseres Reiseassistenten. Wir verkaufen Ihre Daten nie.",
        "Einige dieser Anbieter verarbeiten Daten außerhalb der Türkei und des EU/EWR-Raums; in diesen Fällen stützen wir uns auf Angemessenheitsbeschlüsse oder Standardvertragsklauseln.",
      ],
    },
    {
      title: "Speicherdauer",
      body: [
        "Buchungs- und Rechnungsunterlagen werden gemäß türkischem Buchhaltungs- und Steuerrecht 10 Jahre aufbewahrt. Kontodaten werden für die Dauer des Kontos und bis zu 12 Monate nach einem Löschwunsch gespeichert, um Streitfälle klären zu können. Live-GPS-Positionen werden kurz nach Fahrtende gelöscht. Supportkonversationen werden bis zu 24 Monate gespeichert.",
      ],
    },
    {
      title: "Ihre Rechte",
      body: [
        `Nach dem türkischen Datenschutzgesetz (KVKK Nr. 6698) und, soweit anwendbar, der EU-DSGVO können Sie Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Widerspruch, Datenübertragbarkeit und den Widerruf Ihrer Einwilligung verlangen. Schreiben Sie an ${EMAIL}; wir antworten innerhalb von 30 Tagen. Sie können sich außerdem bei der türkischen Datenschutzbehörde (KVKK) oder Ihrer örtlichen Aufsichtsbehörde beschweren.`,
      ],
    },
    {
      title: "Cookies und lokaler Speicher",
      body: [
        "Wir nutzen den lokalen Speicher des Browsers, um Sie angemeldet zu halten (Authentifizierungs-Token), Ihre Sprachwahl zu speichern und Ihre Supportkonversation zusammenzuhalten. Das ist für den Betrieb der Website unbedingt erforderlich. Werbe-Cookies setzen wir nicht ein.",
      ],
    },
    {
      title: "Sicherheit",
      body: [
        "Der Datenverkehr ist per HTTPS verschlüsselt, Passwörter werden nur als gesalzene Hashes gespeichert, der Zugriff auf Buchungsdaten ist nach Rolle beschränkt (Reisender, Fahrer, Agentur, Administrator) und Kontaktdaten des Fahrers werden nur für die ihm zugewiesene Fahrt angezeigt.",
      ],
    },
    {
      title: "Kinder",
      body: [
        "Unser Angebot richtet sich nicht an Kinder unter 16 Jahren. Kinder reisen als Passagiere einer von einem Erwachsenen getätigten Buchung; wir benötigen lediglich Anzahl und Altersgruppe der Passagiere, um die passenden Kindersitze bereitzustellen.",
      ],
    },
    {
      title: "Änderungen dieser Erklärung",
      body: [
        "Wenn wir diese Erklärung ändern, aktualisieren wir das oben genannte Datum und informieren Sie bei wesentlichen Änderungen vorab per E-Mail oder in der App.",
      ],
    },
  ],
};
